import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  MapPin,
  Building2,
  Shield,
  Award,
  Target,
  Briefcase,
  Calendar,
  Globe,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const USNigerianDiaspora: React.FC = () => {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  // Update page title and meta description
  useEffect(() => {
    document.title = "Nigerian Diaspora in the US | Strategic Investment for Growth";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Join thousands of Nigerians in the US investing back home. Explore trusted projects, wealth-building strategies, and secure investments that drive Nigeria's progress.");
    }
  }, []);

  const faqs = [
    {
      id: 1,
      question: "What are the top sectors for Nigerian diaspora investors in the US?",
      answer: "US-based Nigerians often invest in agriculture, real estate, renewable energy, healthcare, and fintech projects in Nigeria."
    },
    {
      id: 2,
      question: "Can I invest in Nigeria while living in the US without legal complications?",
      answer: "Yes, DiasporaWise ensures compliance with Nigerian and US financial regulations, making cross-border investments easy and legal."
    },
    {
      id: 3,
      question: "How much do I need to start investing from the US?",
      answer: "You can start with flexible amounts depending on the project type, from small-scale investments to large opportunities."
    },
    {
      id: 4,
      question: "How does DiasporaWise protect US investors from fraud?",
      answer: "All projects undergo due diligence, background checks, and monitoring, ensuring only verified and profitable opportunities are listed."
    },
    {
      id: 5,
      question: "Can Nigerian professionals in the US invest collectively?",
      answer: "Yes, DiasporaWise allows groups or associations of Nigerians in the US to pool funds and invest in larger projects."
    }
  ];



  const cities = [
    { name: "New York", investors: "95,000+", description: "The largest concentration of Nigerian professionals in the US" },
    { name: "Houston", investors: "65,000+", description: "Major energy and healthcare hub for Nigerian professionals" },
    { name: "Los Angeles", investors: "45,000+", description: "Entertainment and technology center with growing Nigerian community" },
    { name: "Atlanta", investors: "35,000+", description: "Business and logistics hub attracting Nigerian entrepreneurs" },
    { name: "Chicago", investors: "28,000+", description: "Financial and manufacturing center with strong Nigerian presence" },
    { name: "Dallas", investors: "22,000+", description: "Tech and telecommunications hub with growing Nigerian community" }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "US Tax Compliance",
      description: "Expert guidance on US tax implications, FBAR reporting, and optimization strategies for your Nigerian investments."
    },
    {
      icon: Globe,
      title: "International Transfers",
      description: "Secure and compliant methods for transferring funds from USD to Naira with competitive exchange rates."
    },
    {
      icon: Award,
      title: "Local Support",
      description: "Dedicated US-based team available during US business hours for personalized investment support."
    },
    {
      icon: Users,
      title: "Professional Network",
      description: "Connect with successful Nigerian professionals across the US for networking and collaboration opportunities."
    }
  ];

  const investmentTypes = [
    {
      title: "Real Estate",
      description: "Premium properties in Lagos, Abuja, and other major cities",
      returns: "15-25%",
      icon: Building2
    },
    {
      title: "Technology",
      description: "Startups and tech companies with high growth potential",
      returns: "25-50%",
      icon: TrendingUp
    },
    {
      title: "Agriculture",
      description: "Modern farming and agribusiness opportunities",
      returns: "12-20%",
      icon: Target
    },
    {
      title: "Healthcare",
      description: "Medical facilities and healthcare infrastructure projects",
      returns: "18-30%",
      icon: Briefcase
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Simple Hero Section */}
      <section className="relative py-12 px-6 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
              <MapPin className="w-4 h-4" />
              United States
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Nigerian Diaspora Investment – United States
            </h1>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              Join thousands of Nigerians in the US investing back home. Explore trusted projects, wealth-building strategies, and secure investments that drive Nigeria's progress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.diasporawise.com"
                className="bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-900 transition-all duration-200 flex items-center justify-center gap-2 lg:hidden"
              >
                <Briefcase className="w-5 h-5" />
                Start Investing Today
              </a>
              <Link
                to="/contact"
                className="bg-white border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Contact US Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* US Cities */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nigerian Communities Across the US
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with Nigerian professionals in major US cities and access local networking opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cities.map((city, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{city.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{city.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Investment Opportunities */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Investment Opportunities for US Investors
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access to carefully curated investment opportunities with US-specific considerations and support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentTypes.map((investment, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <investment.icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{investment.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{investment.description}</p>
                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                    {investment.returns} Returns
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* US-Specific Benefits */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              US-Specific Benefits & Support
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored services and support designed specifically for US-based Nigerian investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* US Events & Meetups */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Upcoming US Events
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join our networking events and investment seminars across the US.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                date: "March 18, 2024",
                title: "New York Investment Summit",
                location: "New York, NY",
                description: "Connect with fellow investors and learn about new opportunities"
              },
              {
                date: "March 25, 2024",
                title: "Houston Tech Investment Meetup",
                location: "Houston, TX",
                description: "Focus on technology and energy investment opportunities"
              },
              {
                date: "April 1, 2024",
                title: "Los Angeles Real Estate Seminar",
                location: "Los Angeles, CA",
                description: "Learn about real estate investment strategies and market trends"
              }
            ].map((event, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-red-600" />
                  <span className="text-red-600 font-semibold">{event.date}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{event.location}</span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">{event.description}</p>
                <button className="w-full bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors duration-300">
                  Register Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Invest from the US?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Join thousands of successful Nigerian professionals in the US who are building wealth through strategic investments in Nigeria.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.diasporawise.com"
              className="bg-white text-red-700 px-8 py-4 rounded-xl font-semibold hover:bg-red-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2 lg:hidden"
            >
              <Target className="w-5 h-5" />
              Get Started Now
            </a>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contact US Team
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about investing in Nigeria from the US"
        faqs={faqs}
      />

      {/* Learn More About Nigerian Diaspora Investment in the US Section */}
      <section className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
              aria-expanded={isLearnMoreOpen}
              aria-controls="us-diaspora-content"
            >
              <span className="font-medium">
                {isLearnMoreOpen ? 'Hide Learn More About Nigerian Diaspora Investment in the US' : 'Learn More About Nigerian Diaspora Investment in the US'}
              </span>
              {isLearnMoreOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div
            id="us-diaspora-content"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <article className="prose prose-lg max-w-none">
              {/* Main Content Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Nigerian Diaspora Investment – United States
                </h2>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Introduction – Background to Nigerians in Diaspora and Their Investment into the Home Country</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The United States is home to one of the largest Nigerian diaspora communities, with over 400,000 residents living, studying, and working across America. For decades, they have contributed through remittances, supporting healthcare, education, and family needs back home. While remittances are vital, they often do not create long-term wealth or drive structural economic growth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Today, this is changing. Nigerian diaspora investment in the US is growing rapidly. Instead of simply sending money, US-based Nigerians are now channeling their resources into structured, transparent, and profitable opportunities via platforms like Diasporawise. These investments not only support families but also generate jobs, strengthen infrastructure, and contribute to sustainable national growth.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Nigerian Diaspora Investment in US Perspective</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians in the US, investment is more than a financial lifeline—it is a way to build intergenerational wealth and actively shape Nigeria's future. Unlike remittances, which meet immediate household needs, diaspora investments create lasting assets, stimulate business growth, and empower local communities.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through trusted platforms like Diasporawise, US-based Nigerian investors can access opportunities in real estate, agriculture, technology, renewable energy, and SMEs without relying on middlemen or facing risks of unregulated schemes. These investments are transparent, structured, and designed to maximize both returns and impact.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This trend is redefining the connection between the Nigerian diaspora and their homeland—transforming emotional bonds into long-term financial and developmental contributions.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Best Investments for Nigerians in the US</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Real Estate Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Real estate remains the most attractive sector for diaspora wealth creation. With rapid urbanization and a growing housing demand, real estate investment in Nigeria offers both rental income and long-term capital appreciation.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  US-based Nigerian investors can explore: Residential housing estates and apartments. Commercial office complexes and retail spaces. Land banking for long-term value growth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investing in Nigerian property ensures stable returns and creates generational wealth for diaspora families.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">2. Agriculture Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Agriculture is the backbone of Nigeria's economy and a prime opportunity for Nigerian diaspora investment. Beyond profits, it promotes food security and job creation.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Key opportunities include: Large-scale farming and crop production. Agro-processing and packaging enterprises. Export-driven agribusiness ventures.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians abroad, agriculture investment is both financially rewarding and socially impactful.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">3. Technology & Startups</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's tech ecosystem is booming, making it a hotspot for diaspora investors. From fintech to e-commerce, startups are attracting international recognition and delivering strong returns.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  US-based investors can participate in: Early-stage startup funding. Scalable digital platforms. Fintech and mobile-based solutions.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  By leveraging their global exposure, Nigerians in the US can help local startups grow while securing profitable returns.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Renewable Energy Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's energy challenges have opened doors for massive investment in solar, wind, and other renewable solutions. Diaspora investors are increasingly funding clean energy to meet rising demand.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investment opportunities include: Solar home systems and mini-grids. Wind and biomass energy projects. Sustainable power infrastructure.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These investments not only yield attractive returns but also support Nigeria's clean energy transition.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">5. SMEs and Infrastructure</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Small and medium enterprises (SMEs) are the backbone of Nigeria's economy. Similarly, infrastructure projects create lasting national impact. For US-based Nigerian investors, supporting these sectors means backing businesses that generate jobs and drive industrial growth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Key areas: Transport and logistics companies. Communication and digital infrastructure. SME financing and expansion programs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These sectors offer both strong financial returns and a chance to directly support Nigeria's economic resilience.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Pros of Diaspora Investment for Nigerians in the US</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investing in Nigeria from the US brings multiple advantages:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Wealth Creation</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Beyond remittances, build assets that can be passed on to future generations.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safe Investment Options</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Platforms like Diasporawise ensure transparency and reliability.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">National Growth</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investments fuel job creation, infrastructure, and business expansion.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural & Emotional Connection</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Stay connected to Nigeria while living abroad.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Generational Legacy</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Establish financial security for children and grandchildren.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Begin Investing in Nigeria from USA</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Thanks to digital platforms, investing in Nigeria from the US is easier than ever.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Steps for US-based Nigerian investors:</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  1. Register on trusted platforms like Diasporawise.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  2. Choose a sector—real estate, agriculture, technology, renewable energy, or SMEs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  3. Review details on risks, expected returns, and timelines.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  4. Invest safely via online payment systems.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  5. Monitor your investments with live updates and reports.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This streamlined approach ensures safe, transparent, and profitable engagement for the Nigerian diaspora.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">What Will Become of Nigerian Diaspora Investment in USA</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The outlook for Nigerian diaspora finance is extremely promising:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Digital fintech platforms</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Simplify cross-border investing.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government policies</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Increasingly support diaspora investors.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Rising demand</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For housing, energy, and food ensures steady returns.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Growing diaspora networks</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In the US provide shared insights and opportunities.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Over the next decade, diaspora investment is expected to surpass remittances as the primary driver of Nigeria's economic transformation.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion – Wealth Creation and a Stronger Nigeria</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians in the US, investing in their homeland is more than financial—it's about shaping Nigeria's future. Real estate, agriculture, technology, renewable energy, and SMEs are the pillars of opportunity.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  With platforms like Diasporawise, diaspora investors can confidently build wealth while contributing to Nigeria's growth story. Investing today means leaving a legacy for tomorrow.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default USNigerianDiaspora;
